import { Plus, Trash2 } from 'lucide-react';
import { Button } from '#client/components/ui/Button';
import { TextField } from '#client/components/ui/TextField';

interface LinesFieldProps {
  label: string;
  lines: readonly string[];
  onChange: (lines: string[]) => void;
  max: number;
  placeholder?: string | undefined;
  helpText?: string | undefined;
}

/**
 * A short list of printed lines — a signatory's qualifications, the lab's
 * address, the phone numbers under CONTACT.
 *
 * One input per line rather than a textarea split on newlines. Each of these is
 * a separate typeset line in the PDF, and a textarea invites pasted text with
 * blank lines and trailing whitespace that then have to be guessed about. The
 * ceiling is the schema's, so the control cannot offer what the server refuses.
 */
export function LinesField({
  label,
  lines,
  onChange,
  max,
  placeholder,
  helpText,
}: LinesFieldProps) {
  return (
    <div className="c-lines">
      <p className="c-lines__label">{label}</p>

      {lines.map((line, index) => (
        // Index-keyed on purpose: these are positional slots, not identified
        // records, and there is nothing stable to key on — two blank lines are
        // genuinely the same value.
        <div className="c-lines__row" key={index}>
          <TextField
            label={`${label} line ${index + 1}`}
            hideLabel
            value={line}
            placeholder={placeholder}
            onChange={(event) =>
              onChange(
                lines.map((current, position) =>
                  position === index ? event.target.value : current,
                ),
              )
            }
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 />}
            aria-label={`Remove ${label} line ${index + 1}`}
            onClick={() => onChange(lines.filter((_, position) => position !== index))}
          />
        </div>
      ))}

      {lines.length < max && (
        <Button size="sm" icon={<Plus />} onClick={() => onChange([...lines, ''])}>
          Add line
        </Button>
      )}

      {helpText && <p className="c-lines__help">{helpText}</p>}
    </div>
  );
}
